import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase, onAuthStateChange, getCachedSession, waitForAuthReady } from './supabase';
import { getProfileByEmail, createProfile } from './api';
import { setOfflineUserId } from './offlineStorage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Determine the intended role based on how the user arrived.
 * Student paths (/join/*) and explicit CTA choices set it; when it is
 * unknown, the first-login role selection screen asks the user directly.
 */
const INTENDED_ROLE_KEY = 'draftflow_intended_role';

const getIntendedRole = () => {
  // Check sessionStorage first — survives the OAuth redirect
  const stored = sessionStorage.getItem(INTENDED_ROLE_KEY);
  if (stored) return stored;

  // Fall back to current URL path/query
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);

  if (path.startsWith('/join/')) return 'student';
  if (path === '/join') return 'student';
  if (params.get('role') === 'student') return 'student';

  // Unknown — first sign-in goes through the role selection screen
  return null;
};

// Sentinel: profile doesn't exist and we don't know the intended role.
const NEEDS_ROLE_SELECTION = Symbol('needs-role-selection');

/** Build the profile payload for a Supabase session user. */
const buildProfileData = (sessionUser, role) => ({
  email: sessionUser.email,
  full_name: sessionUser.user_metadata?.full_name || sessionUser.email.split('@')[0],
  avatar_url: sessionUser.user_metadata?.avatar_url,
  google_id: sessionUser.id,
  role,
});

/** Create the profile, absorbing the 409 create race and flagging 403 rejections. */
const createProfileForUser = async (sessionUser, role) => {
  try {
    const created = await createProfile(buildProfileData(sessionUser, role));
    sessionStorage.removeItem(INTENDED_ROLE_KEY);
    return created;
  } catch (createErr) {
    if (createErr.response?.status === 409) {
      // Race condition: profile was created between our fetch and create calls
      try {
        return await getProfileByEmail(sessionUser.email);
      } catch (retryErr) {
        console.error('Could not fetch profile after 409:', retryErr.message);
        return null;
      }
    }

    // 403 = auth rejection (e.g. email mismatch) — clean sign-out path
    if (createErr.response?.status === 403) {
      console.error('Profile creation rejected:', createErr.response?.data?.detail);
      sessionStorage.removeItem(INTENDED_ROLE_KEY);
      // Human-readable message only — never surface the raw backend detail
      const rejection = new Error("We couldn't finish setting up your account. Please sign in again.");
      rejection.isProfileRejection = true;
      throw rejection;
    }

    console.error('Failed to create profile:', createErr.message);
    return null;
  }
};

/**
 * Fetch or create a profile for the given user.
 * Returns NEEDS_ROLE_SELECTION when the profile doesn't exist and the entry
 * path didn't reveal a role — the caller shows the role selection screen.
 * Role is only set on first-time profile creation — existing profiles are not modified.
 */
const fetchOrCreateProfile = async (sessionUser) => {
  // Try to fetch existing profile
  try {
    return await getProfileByEmail(sessionUser.email);
  } catch (fetchErr) {
    if (fetchErr.response?.status !== 404 && fetchErr.response?.status !== 406) {
      // Not a "profile missing" error — could be auth, network, etc.
      console.error('Profile lookup failed:', fetchErr.response?.status, fetchErr.message);
      return null;
    }
  }

  // Profile doesn't exist — create it if the entry path told us the role
  // (join links => student). Otherwise ask the user on the role screen.
  const role = getIntendedRole();
  if (!role) {
    return NEEDS_ROLE_SELECTION;
  }
  return createProfileForUser(sessionUser, role);
};

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage synchronously to prevent flash.
  // CRITICAL: Do NOT restore profile from localStorage for non-demo users.
  // Stale cached role (e.g., demo student) would cause teachers to see the
  // wrong dashboard before checkSession() completes.  Only demo users get
  // their profile restored synchronously — real users always wait for the
  // API to return the fresh profile.
  const getInitialState = () => {
    try {
      const storedUser = localStorage.getItem('draftflow_user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed.isDemo) {
          // Demo users: restore fully (no API call will follow)
          return {
            user: parsed,
            profile: parsed,
            isDemo: true,
            loading: false
          };
        }
        // Real users: keep user ref for quick isAuthenticated check,
        // but NULL the profile so DashboardShell stays in loading state
        // until checkSession() fetches the fresh profile from the API.
        return {
          user: parsed,
          profile: null,
          isDemo: false,
          loading: true
        };
      }
    } catch (e) {
      console.error('Error reading from localStorage:', e);
    }
    return { user: null, profile: null, isDemo: false, loading: true };
  };

  const initialState = getInitialState();
  const [user, setUser] = useState(initialState.user);
  const [profile, setProfile] = useState(initialState.profile);
  const [loading, setLoading] = useState(initialState.loading);
  const [isDemo, setIsDemo] = useState(initialState.isDemo);
  // First sign-in with unknown role: session exists, profile doesn't.
  // The role selection screen resolves this via selectRole().
  const [needsRoleSelection, setNeedsRoleSelection] = useState(false);
  const pendingSessionUser = useRef(null);

  // Guard 4: Deduplicate fetchOrCreateProfile — prevent checkSession and
  // onAuthStateChange from both calling it simultaneously.
  const profileFetchInFlight = useRef(false);

  // Safety timeout — if loading is stuck after 10 seconds, clear stale state
  // and redirect to landing page. This prevents the infinite spinner when
  // Supabase session rehydration hangs (E007).
  useEffect(() => {
    if (!loading) return; // Already resolved, no timeout needed
    const timeout = setTimeout(() => {
      if (!profileFetchInFlight.current) {
        console.warn('Auth loading timeout — clearing stale session and redirecting');
        localStorage.removeItem('draftflow_user');
        setUser(null);
        setProfile(null);
        setIsDemo(false);
        setLoading(false);
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
    }, 10000);
    return () => clearTimeout(timeout);
  }, [loading]);

  useEffect(() => {
    // Demo users were fully restored by getInitialState — skip API refresh
    if (isDemo) return;

    // Shared helper: fetch fresh profile, deduplicated via ref
    const resolveProfile = async (sessionUser) => {
      if (profileFetchInFlight.current) return;
      profileFetchInFlight.current = true;
      try {
        setUser(sessionUser);
        const profileData = await fetchOrCreateProfile(sessionUser);
        if (profileData === NEEDS_ROLE_SELECTION) {
          pendingSessionUser.current = sessionUser;
          setNeedsRoleSelection(true);
        } else if (profileData) {
          setNeedsRoleSelection(false);
          setProfile(profileData);
          setOfflineUserId(profileData.id);
          localStorage.setItem('draftflow_user', JSON.stringify({
            ...profileData,
            isDemo: false
          }));
        }
      } catch (err) {
        if (err.isProfileRejection) {
          // Invalid teacher code or denied — clean sign-out
          console.warn('Profile creation rejected, signing out:', err.message);
          await supabase.auth.signOut();
          setUser(null);
          setProfile(null);
          localStorage.removeItem('draftflow_user');
          // Store the error message for display after redirect
          sessionStorage.setItem('draftflow_auth_error', err.message);
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }
      } finally {
        profileFetchInFlight.current = false;
        setLoading(false);
      }
    };

    // Always check Supabase session and fetch fresh profile from API
    const checkSession = async () => {
      try {
        // Read the cached session (seeded once in supabase.js) instead of
        // calling getSession() here — a second concurrent getSession races the
        // auth-token Web Lock and throws "lock stolen" (Q12 root cause).
        await waitForAuthReady();
        const session = getCachedSession();
        if (session?.user) {
          await resolveProfile(session.user);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Session check error:', error);
        setLoading(false);
      }
    };

    checkSession();

    // Subscribe to auth changes
    const { data: { subscription } } = onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        await resolveProfile(session.user);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setProfile(null);
        setNeedsRoleSelection(false);
        pendingSessionUser.current = null;
        setLoading(false);
        localStorage.removeItem('draftflow_user');
        // Clear offline draft state on all sign-out paths
        import('./offlineStorage').then(({ clearAllLocalDrafts, setOfflineUserId: clearId }) => {
          clearAllLocalDrafts().catch(() => {});
          clearId(null);
        }).catch(() => {});
      }
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInDemo = async (role = 'teacher') => {
    const demoUser = {
      id: `demo-${role}-${Date.now()}`,
      email: role === 'teacher' ? 'demo.teacher@school.edu' : 'demo.student@school.edu',
      full_name: role === 'teacher' ? 'Demo Teacher' : 'Demo Student',
      role: role,
      avatar_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${role}`,
      isDemo: true
    };

    // Demo mode is fully client-side — no backend calls needed.
    // The API interceptor blocks non-profile calls for demo users anyway (E004).
    setUser(demoUser);
    setProfile(demoUser);
    setIsDemo(true);
    localStorage.setItem('draftflow_user', JSON.stringify(demoUser));
  };

  /**
   * Complete first-login role selection: create the profile with the chosen
   * role and land the user in the app. Called by the RoleSelect screen.
   */
  const selectRole = async (role) => {
    const sessionUser = pendingSessionUser.current;
    if (!sessionUser) {
      console.error('selectRole called without a pending session');
      return;
    }
    setLoading(true);
    try {
      const created = await createProfileForUser(sessionUser, role);
      if (created) {
        setProfile(created);
        setNeedsRoleSelection(false);
        pendingSessionUser.current = null;
        setOfflineUserId(created.id);
        localStorage.setItem('draftflow_user', JSON.stringify({ ...created, isDemo: false }));
      }
    } catch (err) {
      if (err.isProfileRejection) {
        await supabase.auth.signOut();
        setUser(null);
        setProfile(null);
        setNeedsRoleSelection(false);
        pendingSessionUser.current = null;
        localStorage.removeItem('draftflow_user');
        sessionStorage.setItem('draftflow_auth_error', err.message);
        window.location.href = '/';
      }
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async (intendedRole = null, { promptSelectAccount = false } = {}) => {
    // Guard 1: Clear stale localStorage before OAuth redirect.
    // Prevents getInitialState from restoring a stale demo/student profile
    // when the user returns from Google OAuth as a teacher.
    localStorage.removeItem('draftflow_user');

    // Guard 2: Persist intended role to sessionStorage when the entry point
    // reveals it (e.g. a /join link => student). When unknown, leave it unset —
    // the first-login role selection screen will ask.
    const role = intendedRole || getIntendedRole();
    if (role) {
      sessionStorage.setItem(INTENDED_ROLE_KEY, role);
    } else {
      sessionStorage.removeItem(INTENDED_ROLE_KEY);
    }

    // Identity-only login — Drive/Classroom scopes are requested
    // just-in-time when the teacher connects those features.
    // promptSelectAccount forces Google's account chooser (used by the join-link
    // role-mismatch screen so the user can pick a DIFFERENT account, Q13).
    const options = {
      scopes: 'openid profile email',
      redirectTo: window.location.origin + '/auth/callback',
    };
    if (promptSelectAccount) {
      options.queryParams = { prompt: 'select_account' };
    }
    const { data, error } = await supabase.auth.signInWithOAuth({ provider: 'google', options });
    return { data, error };
  };

  const signOut = async () => {
    if (!isDemo) {
      await supabase.auth.signOut();
    }
    // Clear offline drafts and user scope to prevent data leaking on shared devices
    try {
      const { clearAllLocalDrafts, setOfflineUserId: clearUserId } = await import('./offlineStorage');
      await clearAllLocalDrafts();
      clearUserId(null);
    } catch (e) {
      console.warn('Failed to clear offline drafts on sign-out:', e);
    }
    setUser(null);
    setProfile(null);
    setIsDemo(false);
    setNeedsRoleSelection(false);
    pendingSessionUser.current = null;
    setLoading(false);
    localStorage.removeItem('draftflow_user');
  };

  const value = {
    user,
    profile,
    loading,
    isDemo,
    isAuthenticated: !!user,
    isTeacher: profile?.role === 'teacher',
    isStudent: profile?.role === 'student',
    needsRoleSelection,
    selectRole,
    signInDemo,
    signInWithGoogle,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
